import { IAuthUser } from "intake_shared/src/types";
import { AppRoute } from "./routes";
import { isActiveCareCoordinator } from "intake_shared/src/assignment";

export function getAllowedRoutes(authUser?: IAuthUser): Set<AppRoute> {
    if (!authUser || authUser.deleted || authUser.acl?.blocked) {
        return new Set();
    }

    const hasOtherEnabledPermission = Object.entries(authUser.acl || {}).some(
        ([permission, enabled]) =>
            permission !== "care_coordinator" &&
            permission !== "can_be_auto_assigned" &&
            permission !== "blocked" &&
            enabled === true
    );
    const careRoleOnly =
        authUser.acl?.care_coordinator === true && !hasOtherEnabledPermission;
    const canUserAccesApp =
        Boolean(authUser.clinician) || (Boolean(authUser.acl) && !careRoleOnly);
    const activeAdmin =
        authUser.acl?.super_admin === true &&
        !authUser.acl.blocked &&
        !authUser.deleted;

    const allowedRoutes: AppRoute[] = [];
    switch (true) {
        case authUser.acl?.super_admin:
            allowedRoutes.push(
                AppRoute.BOOKED_CLIENTS,
                AppRoute.CLIENT_FULL_VIEW,
                AppRoute.CLIENT_MASTER_LIST,
                AppRoute.CLINICIANS,
                AppRoute.CLINICIAN_AVAILABILITY,
                AppRoute.CONFIGURATIONS_HOME,
                AppRoute.DIAGNOSTICS,
                AppRoute.EXPERIENCE_CONFIG,
                AppRoute.INTAKE_OTHER,
                AppRoute.FIND_CLINICIAN,
                AppRoute.INACTIVE_CLIENT_VIEW,
                AppRoute.INTAKE_CLINCIAN_AVAILABILITY,
                AppRoute.INTAKE_QUEUE,
                AppRoute.MAIN,
                AppRoute.MANAGE_SUPERVISEE,
                AppRoute.OFFICE_WINDOWS,
                AppRoute.OUTBOUND_EMAILS,
                AppRoute.SUPERVISEES,
                AppRoute.UNAUTHORISED,
                AppRoute.USER_MANAGEMENT,
                AppRoute.EMAIL_SIGNATURES,
                AppRoute.WAITLIST,
                AppRoute.OFFICE_MANAGEMENT,
                AppRoute.REQUEST_OFFICE_WINDOW
            );
            break;
        case authUser.acl?.office_manager:
            allowedRoutes.push(
                AppRoute.OFFICE_MANAGEMENT,
                AppRoute.INTAKE_QUEUE,
                AppRoute.UNAUTHORISED,
                AppRoute.OFFICE_WINDOWS,
                AppRoute.CLINICIAN_AVAILABILITY,
                AppRoute.REQUEST_OFFICE_WINDOW,
                AppRoute.MAIN
            );
            break;
        case authUser.acl?.intake:
            allowedRoutes.push(
                AppRoute.FIND_CLINICIAN,
                AppRoute.INTAKE_QUEUE,
                AppRoute.WAITLIST,
                AppRoute.CLIENT_MASTER_LIST,
                AppRoute.CLIENT_FULL_VIEW,
                AppRoute.MAIN,
                AppRoute.INACTIVE_CLIENT_VIEW,
                AppRoute.EMAIL_SIGNATURES,
                AppRoute.OUTBOUND_EMAILS,
                AppRoute.UNAUTHORISED,
                AppRoute.CLINICIAN_AVAILABILITY,
                AppRoute.INTAKE_CLINCIAN_AVAILABILITY,
                AppRoute.EXPERIENCE_CONFIG,
                AppRoute.INTAKE_OTHER,
                AppRoute.SUPERVISEES,
                AppRoute.MANAGE_SUPERVISEE
            );
            break;
        case authUser.acl?.insurance_verification:
            allowedRoutes.push(
                AppRoute.CLIENT_FULL_VIEW,
                AppRoute.CLIENT_MASTER_LIST,
                AppRoute.EXPERIENCE_CONFIG,
                AppRoute.FIND_CLINICIAN,
                AppRoute.INACTIVE_CLIENT_VIEW,
                AppRoute.INTAKE_CLINCIAN_AVAILABILITY,
                AppRoute.INTAKE_QUEUE,
                AppRoute.MAIN,
                AppRoute.UNAUTHORISED
            );
            break;
        case authUser.acl?.billing:
            allowedRoutes.push(
                AppRoute.CLIENT_FULL_VIEW,
                AppRoute.CLIENT_MASTER_LIST,
                AppRoute.FIND_CLINICIAN,
                AppRoute.INACTIVE_CLIENT_VIEW,
                AppRoute.INTAKE_CLINCIAN_AVAILABILITY,
                AppRoute.INTAKE_QUEUE,
                AppRoute.MAIN,
                AppRoute.UNAUTHORISED
            );
            break;
        case authUser.acl?.copay:
            allowedRoutes.push(
                AppRoute.CLIENT_FULL_VIEW,
                AppRoute.CLIENT_MASTER_LIST,
                AppRoute.INACTIVE_CLIENT_VIEW,
                AppRoute.INTAKE_QUEUE,
                AppRoute.MAIN,
                AppRoute.UNAUTHORISED
            );
            break;
        case canUserAccesApp:
            // Everyone else who is a clinician or has acl rights
            allowedRoutes.push(
                AppRoute.CLINICIAN_AVAILABILITY,
                AppRoute.REQUEST_OFFICE_WINDOW,
                AppRoute.MAIN,
                AppRoute.MANAGE_SUPERVISEE,
                AppRoute.SUPERVISEES,
                AppRoute.UNAUTHORISED
            );
            break;
        default:
            // Anyone who does not have acl permission or clinician rights.
            allowedRoutes.push(AppRoute.UNAUTHORISED);
    }
    if (activeAdmin) {
        allowedRoutes.push(AppRoute.CARE_COORDINATORS);
    }
    if (authUser.user?.email && (isActiveCareCoordinator(authUser) || activeAdmin)) {
        allowedRoutes.push(AppRoute.MAIN, AppRoute.TIME_OFF);
    }
    return new Set(allowedRoutes);
}
